import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Drawer,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import { FC, ReactNode, useEffect, useMemo, useState } from "react";

import { AppRole, type ICardImportDetail } from "@monorepo/core";

import ManaIdentityIndicator from "./mana-identity-indicator.component";
import StatusChip from "./status-chip.component";
import useAuth from "../hooks/auth.hook";
import {
  useCancelCardImport,
  useCardImportCardStatuses,
  useDeleteCardImportHistory,
} from "../hooks/card-import.hook";
import {
  formatCardImportBytes,
  formatCardImportTimestamp,
  getCardImportSourceLabel,
  isActiveImportStatus,
} from "../utils/card-import-display";
import { displayFontFamily } from "../theme/mtgTheme";

const CARD_STATUS_PAGE_SIZE = 25;
const DEFAULT_CARD_STATUS_SKELETON_COUNT = 6;

interface CardImportDetailDrawerProps {
  importId?: string;
  detail?: ICardImportDetail;
  isLoading: boolean;
  error?: { message: string } | null;
  onClose: () => void;
}

const DetailStatCard = ({
  label,
  value,
}: {
  label: string;
  value: ReactNode;
}) => (
  <Card>
    <CardContent sx={{ p: 1.25, "&:last-child": { pb: 1.25 } }}>
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        rowGap={0.5}
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography
          variant="caption"
          sx={{
            flex: "1 1 auto",
            minWidth: 0,
            color: "text.secondary",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            lineHeight: 1.2,
          }}
        >
          {label}
        </Typography>
        {typeof value === "string" || typeof value === "number" ? (
          <Typography
            variant="body2"
            sx={{
              maxWidth: "100%",
              fontWeight: 700,
              textAlign: "right",
              lineHeight: 1.2,
            }}
          >
            {value}
          </Typography>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              maxWidth: "100%",
              minWidth: 0,
              ml: "auto",
            }}
          >
            {value}
          </Box>
        )}
      </Stack>
    </CardContent>
  </Card>
);

const DetailRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      gap: 2,
      p: 1.5,
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 2,
      flexDirection: { xs: "column", sm: "row" },
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontWeight: 600,
        wordBreak: "break-word",
        textAlign: { sm: "right" },
      }}
    >
      {value}
    </Typography>
  </Box>
);

const CompactDetailField = ({
  label,
  value,
  sx,
}: {
  label: string;
  value: ReactNode;
  sx?: SxProps<Theme>;
}) => (
  <Stack spacing={0.35} sx={{ minWidth: 0, ...sx }}>
    <Typography
      variant="caption"
      sx={{
        color: "text.secondary",
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        lineHeight: 1.2,
      }}
    >
      {label}
    </Typography>
    {typeof value === "string" || typeof value === "number" ? (
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, wordBreak: "break-word", lineHeight: 1.35 }}
      >
        {value}
      </Typography>
    ) : (
      <Box sx={{ minWidth: 0 }}>{value}</Box>
    )}
  </Stack>
);

const CardStatusListSkeleton: FC<{
  count: number;
  canViewCardDiagnostics: boolean;
  canViewCardIdentifiers: boolean;
}> = ({ count, canViewCardDiagnostics, canViewCardIdentifiers }) => (
  <Stack spacing={1.5} aria-hidden="true">
    {Array.from({ length: count }, (_, index) => (
      <Box
        key={`card-status-skeleton-${index}`}
        sx={{
          p: 1.5,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
        }}
      >
        <Stack spacing={1.25}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ sm: "flex-start" }}
          >
            <Stack spacing={0.75} sx={{ minWidth: 0, flex: 1 }}>
              <Skeleton variant="text" width="52%" />
              <Skeleton variant="text" width="34%" />
              <Skeleton variant="rounded" width={68} height={20} />
              {canViewCardIdentifiers ? (
                <Skeleton variant="text" width="62%" />
              ) : null}
            </Stack>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
              {canViewCardDiagnostics ? (
                <Skeleton variant="rounded" width={72} height={24} />
              ) : null}
              <Skeleton variant="rounded" width={104} height={24} />
            </Stack>
          </Stack>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: canViewCardIdentifiers
                  ? "repeat(2, minmax(0, 1fr))"
                  : "minmax(120px, 170px) minmax(0, 1fr)",
              },
              gap: 1,
            }}
          >
            <Skeleton variant="text" width={120} />
            <Skeleton
              variant="text"
              width={canViewCardIdentifiers ? 144 : "82%"}
            />
          </Box>
          {canViewCardIdentifiers ? (
            <Skeleton variant="text" width="82%" />
          ) : null}
        </Stack>
      </Box>
    ))}
  </Stack>
);

const CardImportDetailDrawer: FC<CardImportDetailDrawerProps> = ({
  importId,
  detail,
  isLoading,
  error,
  onClose,
}) => {
  const { isAdmin, viewRole } = useAuth();
  const [cardStatusPageIndex, setCardStatusPageIndex] = useState(0);
  const [isCardStatusPageTransitioning, setIsCardStatusPageTransitioning] =
    useState(false);
  const [cardStatusCursors, setCardStatusCursors] = useState<
    (string | undefined)[]
  >([undefined]);

  useEffect(() => {
    setCardStatusPageIndex(0);
    setIsCardStatusPageTransitioning(false);
    setCardStatusCursors([undefined]);
  }, [importId]);

  const importState = detail?.importState;
  const currentCursor = cardStatusCursors[cardStatusPageIndex];
  const cardStatusesQuery = useCardImportCardStatuses(
    importId
      ? {
          importId,
          cursor: currentCursor,
          pageSize: CARD_STATUS_PAGE_SIZE,
        }
      : undefined,
    {
      refetchInterval: isActiveImportStatus(importState?.status) ? 3000 : false,
    },
  );

  useEffect(() => {
    if (!cardStatusesQuery.isFetching) {
      setIsCardStatusPageTransitioning(false);
    }
  }, [cardStatusesQuery.isFetching]);

  const paginatedStatuses = cardStatusesQuery.data?.items ?? [];

  const pageStatusCounts = useMemo(() => {
    const counts = new Map<string, number>();
    paginatedStatuses.forEach((statusItem) => {
      counts.set(statusItem.status, (counts.get(statusItem.status) ?? 0) + 1);
    });
    return Array.from(counts.entries()).sort(([left], [right]) =>
      left.localeCompare(right),
    );
  }, [paginatedStatuses]);
  const pageStageCounts = useMemo(() => {
    const counts = new Map<string, number>();
    paginatedStatuses.forEach((statusItem) => {
      counts.set(statusItem.stage, (counts.get(statusItem.stage) ?? 0) + 1);
    });
    return Array.from(counts.entries()).sort(([left], [right]) =>
      left.localeCompare(right),
    );
  }, [paginatedStatuses]);
  const totalStatusCounts = useMemo(() => {
    const storedCounts = importState?.cardStatusCountsByStatus;
    if (!storedCounts) {
      return pageStatusCounts;
    }

    return Object.entries(storedCounts)
      .filter(([, count]) => typeof count === "number" && count > 0)
      .sort(([left], [right]) => left.localeCompare(right));
  }, [importState?.cardStatusCountsByStatus, pageStatusCounts]);
  const totalStageCounts = useMemo(() => {
    const storedCounts = importState?.cardStatusCountsByStage;
    if (!storedCounts) {
      return pageStageCounts;
    }

    return Object.entries(storedCounts)
      .filter(([, count]) => typeof count === "number" && count > 0)
      .sort(([left], [right]) => left.localeCompare(right));
  }, [importState?.cardStatusCountsByStage, pageStageCounts]);

  const cardStatusTotalCount =
    importState?.cardStatusTotalCount ?? importState?.uniqueCardCount;
  const totalCardStatusPages = cardStatusTotalCount
    ? Math.max(1, Math.ceil(cardStatusTotalCount / CARD_STATUS_PAGE_SIZE))
    : undefined;
  const hasVisibleCardStatusPage =
    paginatedStatuses.length > 0 || isCardStatusPageTransitioning;
  const cardStatusRangeStart = hasVisibleCardStatusPage
    ? cardStatusPageIndex * CARD_STATUS_PAGE_SIZE + 1
    : 0;
  const cardStatusSkeletonCount =
    cardStatusTotalCount && cardStatusRangeStart > 0
      ? Math.max(
          1,
          Math.min(
            CARD_STATUS_PAGE_SIZE,
            cardStatusTotalCount - cardStatusRangeStart + 1,
          ),
        )
      : paginatedStatuses.length > 0
        ? paginatedStatuses.length
        : DEFAULT_CARD_STATUS_SKELETON_COUNT;
  const showCardStatusSkeletons =
    cardStatusesQuery.isLoading || isCardStatusPageTransitioning;
  const cardStatusRangeEnd =
    cardStatusRangeStart === 0
      ? 0
      : cardStatusRangeStart +
        (showCardStatusSkeletons
          ? cardStatusSkeletonCount
          : paginatedStatuses.length) -
        1;
  const sourceLabel = importState
    ? getCardImportSourceLabel(importState)
    : "N/A";
  const canViewStorageDetails = viewRole === AppRole.Admin;
  const canViewCardIdentifiers = viewRole === AppRole.Admin;
  const canViewCardDiagnostics = viewRole === AppRole.Admin;
  const { cancelImport, isCanceling } = useCancelCardImport();
  const { deleteHistory, isDeleting } = useDeleteCardImportHistory();
  const canCancelImport = Boolean(
    importId && isActiveImportStatus(importState?.status),
  );
  const canDeleteHistory = Boolean(
    isAdmin && importId && !isActiveImportStatus(importState?.status),
  );
  const drawerTitle = !importId
    ? "Select an import"
    : canViewCardDiagnostics
      ? importId
      : sourceLabel ?? "Import detail";

  return (
    <Drawer
      anchor="right"
      open={Boolean(importId)}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100vw", md: 720 },
          maxWidth: "100vw",
          p: { xs: 2.5, md: 3 },
          backgroundColor: "background.default",
        },
      }}
    >
      <Stack spacing={3} sx={{ height: "100%", overflowY: "auto" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 1, sm: 1.25 }}
          justifyContent="space-between"
          alignItems={{ sm: "center" }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="overline"
              sx={{ color: "text.secondary", letterSpacing: "0.12em" }}
            >
              Import detail
            </Typography>
            <Typography
              variant="subtitle1"
              title={drawerTitle}
              noWrap
              sx={{
                fontSize: { xs: "1rem", sm: "1.05rem" },
                fontWeight: 700,
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {drawerTitle}
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={0.5}
            useFlexGap
            justifyContent={{ xs: "flex-start", sm: "flex-end" }}
            sx={{ flexShrink: 0, flexWrap: { xs: "wrap", sm: "nowrap" } }}
          >
            {canCancelImport ? (
              <Button
                type="button"
                size="small"
                color="secondary"
                variant="outlined"
                sx={{ minWidth: 0, px: 1.25 }}
                disabled={isLoading || isCanceling}
                onClick={async () => {
                  if (!importId) {
                    return;
                  }

                  const confirmed = window.confirm(
                    "Cancel this import? Any active upload or worker processing will be marked as failed so you can start a new upload.",
                  );
                  if (!confirmed) {
                    return;
                  }

                  await cancelImport({
                    importId,
                    reason: "Import canceled by user.",
                  });
                }}
              >
                {isCanceling ? "Canceling..." : "Cancel import"}
              </Button>
            ) : null}
            {isAdmin ? (
              <Button
                type="button"
                size="small"
                color="secondary"
                variant="outlined"
                sx={{ minWidth: 0, px: 1.25 }}
                disabled={isLoading || !canDeleteHistory || isDeleting}
                onClick={async () => {
                  if (!importId) {
                    return;
                  }

                  const confirmed = window.confirm(
                    "Delete this import? This removes the import history, card statuses, imported cards and tag indexes from DynamoDB, and any uploaded CSV from S3.",
                  );
                  if (!confirmed) {
                    return;
                  }

                  await deleteHistory(importId);
                  onClose();
                }}
              >
                {isDeleting ? "Deleting..." : "Delete import"}
              </Button>
            ) : null}
            <Button
              variant="outlined"
              size="small"
              type="button"
              sx={{ minWidth: 0, px: 1.25 }}
              onClick={onClose}
            >
              Close
            </Button>
          </Stack>
        </Stack>

        {!importId ? (
          <Alert severity="info">
            Select an active or historical import to inspect status and card
            writes.
          </Alert>
        ) : null}

        {importId && isLoading ? (
          <Alert severity="info">Loading import detail...</Alert>
        ) : null}

        {importId && error ? (
          <Alert severity="error">{error.message}</Alert>
        ) : null}

        {detail ? (
          <Stack spacing={3}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, minmax(0, 1fr))",
                  sm: "repeat(4, minmax(0, 1fr))",
                },
                gap: 1,
              }}
            >
              <DetailStatCard
                label="Status"
                value={
                  importState?.status ? (
                    <StatusChip
                      status={importState.status}
                      size="small"
                      sx={{
                        fontSize: "0.62rem",
                        "& .MuiChip-label": {
                          px: 0.75,
                        },
                      }}
                    />
                  ) : (
                    "N/A"
                  )
                }
              />
              <DetailStatCard
                label="Source"
                value={importState?.sourceType ?? "N/A"}
              />
              <DetailStatCard
                label="Rows"
                value={importState?.rowCount ?? "N/A"}
              />
              <DetailStatCard
                label="Unique cards"
                value={importState?.uniqueCardCount ?? "N/A"}
              />
            </Box>

            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h6">Lifecycle</Typography>
                  <DetailRow
                    label="Status message"
                    value={importState?.statusMessage ?? "N/A"}
                  />
                  <DetailRow
                    label="Started"
                    value={formatCardImportTimestamp(importState?.startedAt, {
                      longMonthDay: true,
                    })}
                  />
                  <DetailRow
                    label="Upload completed"
                    value={formatCardImportTimestamp(
                      importState?.uploadCompletedAt,
                      {
                        longMonthDay: true,
                      },
                    )}
                  />
                  <DetailRow
                    label="Queued"
                    value={formatCardImportTimestamp(importState?.queuedAt, {
                      longMonthDay: true,
                    })}
                  />
                  <DetailRow
                    label="Processing started"
                    value={formatCardImportTimestamp(
                      importState?.processingStartedAt,
                      {
                        longMonthDay: true,
                      },
                    )}
                  />
                  <DetailRow
                    label="Completed"
                    value={formatCardImportTimestamp(importState?.completedAt, {
                      longMonthDay: true,
                    })}
                  />
                  <DetailRow
                    label="Updated"
                    value={formatCardImportTimestamp(importState?.updatedAt, {
                      longMonthDay: true,
                    })}
                  />
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h6">Source info</Typography>
                  <DetailRow label="Label" value={sourceLabel} />
                  <DetailRow
                    label="File size"
                    value={formatCardImportBytes(importState?.fileSizeBytes)}
                  />
                  {canViewStorageDetails ? (
                    <DetailRow
                      label="Storage bucket"
                      value={importState?.storageBucket ?? "N/A"}
                    />
                  ) : null}
                  {canViewStorageDetails ? (
                    <DetailRow
                      label="Storage key"
                      value={importState?.storageKey ?? "N/A"}
                    />
                  ) : null}
                </Stack>
              </CardContent>
            </Card>

            {importState?.error ? (
              <Card>
                <CardContent>
                  <Stack spacing={2}>
                    <Typography variant="h6">Failure</Typography>
                    <Box
                      component="pre"
                      sx={(theme) => ({
                        m: 0,
                        p: 2,
                        borderRadius: 2,
                        overflow: "auto",
                        fontSize: 12,
                        lineHeight: 1.6,
                        backgroundColor:
                          theme.palette.mode === "dark" ? "#0D1117" : "#F7F4ED",
                        border: "1px solid",
                        borderColor: "divider",
                      })}
                    >
                      {JSON.stringify(importState.error, null, 2)}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            ) : null}

            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h6">Card status totals</Typography>
                  {cardStatusesQuery.isLoading ? (
                    <Alert severity="info">Loading card status counts...</Alert>
                  ) : cardStatusesQuery.error ? (
                    <Alert severity="error">
                      {cardStatusesQuery.error.message}
                    </Alert>
                  ) : totalStatusCounts.length === 0 &&
                    (!canViewCardDiagnostics ||
                      totalStageCounts.length === 0) ? (
                    <Alert severity="info">
                      No card status rows were written.
                    </Alert>
                  ) : (
                    <Stack spacing={1.5}>
                      {totalStatusCounts.length > 0 ? (
                        <Stack spacing={1}>
                          <Typography variant="body2" color="text.secondary">
                            Status
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={1}
                            useFlexGap
                            flexWrap="wrap"
                          >
                            {totalStatusCounts.map(([status, count]) => (
                              <StatusChip
                                key={status}
                                status={status}
                                label={`${status.replace(/_/g, " ")} · ${count}`}
                              />
                            ))}
                          </Stack>
                        </Stack>
                      ) : null}
                      {canViewCardDiagnostics && totalStageCounts.length > 0 ? (
                        <Stack spacing={1}>
                          <Typography variant="body2" color="text.secondary">
                            Stage
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={1}
                            useFlexGap
                            flexWrap="wrap"
                          >
                            {totalStageCounts.map(([stage, count]) => (
                              <Chip
                                key={stage}
                                label={`${stage} · ${count}`}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Stack>
                        </Stack>
                      ) : null}
                    </Stack>
                  )}
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h6">Card statuses</Typography>
                  {cardStatusesQuery.error ? (
                    <Alert severity="error">
                      {cardStatusesQuery.error.message}
                    </Alert>
                  ) : showCardStatusSkeletons ? (
                    <>
                      {hasVisibleCardStatusPage ? (
                        <Stack
                          direction={{ xs: "column", md: "row" }}
                          spacing={1.5}
                          justifyContent="space-between"
                          alignItems={{ md: "center" }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            {cardStatusTotalCount
                              ? `Showing ${cardStatusRangeStart}-${cardStatusRangeEnd} of ${cardStatusTotalCount}`
                              : `Showing ${cardStatusRangeStart}-${cardStatusRangeEnd}`}
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Button variant="outlined" type="button" disabled>
                              Previous
                            </Button>
                            <Typography variant="body2" color="text.secondary">
                              {totalCardStatusPages
                                ? `Page ${cardStatusPageIndex + 1} of ${totalCardStatusPages}`
                                : `Page ${cardStatusPageIndex + 1}`}
                            </Typography>
                            <Button variant="outlined" type="button" disabled>
                              Next
                            </Button>
                          </Stack>
                        </Stack>
                      ) : null}
                      <CardStatusListSkeleton
                        count={cardStatusSkeletonCount}
                        canViewCardDiagnostics={canViewCardDiagnostics}
                        canViewCardIdentifiers={canViewCardIdentifiers}
                      />
                    </>
                  ) : paginatedStatuses.length === 0 ? (
                    <Alert severity="info">No card-level statuses yet.</Alert>
                  ) : (
                    <>
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={1.5}
                        justifyContent="space-between"
                        alignItems={{ md: "center" }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {cardStatusTotalCount
                            ? `Showing ${cardStatusRangeStart}-${cardStatusRangeEnd} of ${cardStatusTotalCount}`
                            : `Showing ${cardStatusRangeStart}-${cardStatusRangeEnd}`}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Button
                            variant="outlined"
                            type="button"
                            onClick={() => {
                              setIsCardStatusPageTransitioning(true);
                              setCardStatusPageIndex((currentPage) =>
                                Math.max(0, currentPage - 1),
                              );
                            }}
                            disabled={
                              cardStatusPageIndex === 0 ||
                              isCardStatusPageTransitioning
                            }
                          >
                            Previous
                          </Button>
                          <Typography variant="body2" color="text.secondary">
                            {totalCardStatusPages
                              ? `Page ${cardStatusPageIndex + 1} of ${totalCardStatusPages}`
                              : `Page ${cardStatusPageIndex + 1}`}
                          </Typography>
                          <Button
                            variant="outlined"
                            type="button"
                            onClick={() => {
                              const nextCursor =
                                cardStatusesQuery.data?.nextCursor;
                              if (!nextCursor) {
                                return;
                              }

                              setIsCardStatusPageTransitioning(true);
                              setCardStatusCursors((currentCursors) => {
                                const nextCursors = currentCursors.slice(
                                  0,
                                  cardStatusPageIndex + 1,
                                );
                                nextCursors.push(nextCursor);
                                return nextCursors;
                              });
                              setCardStatusPageIndex(
                                (currentPage) => currentPage + 1,
                              );
                            }}
                            disabled={
                              !cardStatusesQuery.data?.nextCursor ||
                              isCardStatusPageTransitioning ||
                              cardStatusesQuery.isFetching
                            }
                          >
                            Next
                          </Button>
                        </Stack>
                      </Stack>

                      <Stack spacing={1.5}>
                        {paginatedStatuses.map((statusItem) => {
                          const updatedLabel = formatCardImportTimestamp(
                            statusItem.updatedAt,
                          );
                          const errorMessage =
                            statusItem.errorMessage?.trim() || undefined;
                          const displayName =
                            statusItem.name ??
                            (canViewCardIdentifiers
                              ? statusItem.scryfallId
                              : "Imported card");

                          return (
                            <Box
                              key={`${statusItem.scryfallId}-${statusItem.stage}-${statusItem.status}-${statusItem.updatedAt}`}
                              sx={{
                                p: 1.5,
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 2,
                              }}
                            >
                              <Stack spacing={1.25}>
                                <Stack
                                  direction={{ xs: "column", sm: "row" }}
                                  spacing={1}
                                  justifyContent="space-between"
                                  alignItems={{ sm: "flex-start" }}
                                >
                                  <Stack spacing={0.75} sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography
                                      variant="subtitle2"
                                      fontWeight={700}
                                      sx={{
                                        fontFamily: displayFontFamily,
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {displayName}
                                    </Typography>
                                    {statusItem.typeLine ? (
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ wordBreak: "break-word" }}
                                      >
                                        {statusItem.typeLine}
                                      </Typography>
                                    ) : null}
                                    <ManaIdentityIndicator
                                      colors={statusItem.colors}
                                    />
                                  </Stack>
                                  <Stack
                                    spacing={0.75}
                                    alignItems={{ xs: "flex-start", sm: "flex-end" }}
                                  >
                                    <Stack
                                      direction="row"
                                      spacing={0.75}
                                      useFlexGap
                                      flexWrap="wrap"
                                      justifyContent={{ xs: "flex-start", sm: "flex-end" }}
                                    >
                                      {canViewCardDiagnostics ? (
                                        <Chip
                                          label={statusItem.stage}
                                          size="small"
                                          variant="outlined"
                                        />
                                      ) : null}
                                      <StatusChip
                                        status={statusItem.status}
                                        size="small"
                                      />
                                    </Stack>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        textAlign: { sm: "right" },
                                        lineHeight: 1.35,
                                      }}
                                    >
                                      Updated {updatedLabel}
                                    </Typography>
                                  </Stack>
                                </Stack>
                                {canViewCardIdentifiers ? (
                                  <Box
                                    sx={{
                                      display: "grid",
                                      gridTemplateColumns: {
                                        xs: "1fr",
                                        sm: "minmax(0, 1fr) minmax(0, 1fr)",
                                      },
                                      gap: 1,
                                      width: "100%",
                                    }}
                                  >
                                    <CompactDetailField
                                      label="Card ID"
                                      value={
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ wordBreak: "break-all" }}
                                        >
                                          {statusItem.scryfallId}
                                        </Typography>
                                      }
                                    />
                                    <CompactDetailField
                                      label="Oracle ID"
                                      value={
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{
                                            wordBreak: "break-all",
                                            textAlign: { sm: "right" },
                                          }}
                                        >
                                          {statusItem.oracleId ?? "N/A"}
                                        </Typography>
                                      }
                                      sx={{ alignItems: { sm: "flex-end" } }}
                                    />
                                  </Box>
                                ) : null}
                                {errorMessage ? (
                                  <CompactDetailField
                                    label="Message"
                                    value={
                                      <Typography
                                        variant="body2"
                                        color="text.primary"
                                        sx={{ wordBreak: "break-word" }}
                                      >
                                        {errorMessage}
                                      </Typography>
                                    }
                                  />
                                ) : null}
                              </Stack>
                            </Box>
                          );
                        })}
                      </Stack>
                    </>
                  )}
                </Stack>
              </CardContent>
            </Card>

            {canViewCardDiagnostics ? (
              <Card>
                <CardContent>
                  <Stack spacing={2}>
                    <Typography variant="h6">Current state snapshot</Typography>
                    <Box
                      component="pre"
                      sx={(theme) => ({
                        m: 0,
                        p: 2,
                        borderRadius: 2,
                        overflow: "auto",
                        fontSize: 12,
                        lineHeight: 1.6,
                        backgroundColor:
                          theme.palette.mode === "dark" ? "#0D1117" : "#F7F4ED",
                        border: "1px solid",
                        borderColor: "divider",
                      })}
                    >
                      {JSON.stringify(detail.currentState ?? null, null, 2)}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            ) : null}
          </Stack>
        ) : null}
      </Stack>
    </Drawer>
  );
};

export default CardImportDetailDrawer;
